import { Brain, Heart, Users, Activity, BookOpen, Flag } from "lucide-react";

const domains = [
  { icon: Brain, name: "Cognitive", desc: "Thinking, curiosity, early literacy & numeracy", color: "from-sky-500 to-blue-600", ring: "ring-sky-100" },
  { icon: Heart, name: "Emotional", desc: "Self-awareness, resilience, joy & calm", color: "from-rose-500 to-pink-600", ring: "ring-rose-100" },
  { icon: Users, name: "Social", desc: "Sharing, empathy, cooperation, friendship", color: "from-amber-500 to-orange-600", ring: "ring-amber-100" },
  { icon: Activity, name: "Physical", desc: "Motor skills, yoga, movement, nutrition", color: "from-emerald-500 to-green-600", ring: "ring-emerald-100" },
  { icon: BookOpen, name: "Cultural", desc: "Sanskrit, festivals, storytelling, rituals", color: "from-fuchsia-500 to-purple-600", ring: "ring-fuchsia-100" },
  { icon: Flag, name: "Moral", desc: "Sanskaar, honesty, kindness, patriotism", color: "from-red-600 to-orange-700", ring: "ring-red-100" }
];

const schedule = [
  { time: "8:30", label: "Warm Welcome", note: "Morning circle & smiles", color: "bg-orange-100 text-orange-800", ring: "border-orange-300" },
  { time: "8:45", label: "Prayer & Shloka", note: "Sanskrit + gratitude", color: "bg-amber-100 text-amber-800", ring: "border-amber-300" },
  { time: "9:00", label: "Play-Based Learning", note: "Language, math, sensory", color: "bg-sky-100 text-sky-800", ring: "border-sky-300" },
  { time: "10:00", label: "Snack & Story", note: "Mindful eating + Panchatantra", color: "bg-rose-100 text-rose-800", ring: "border-rose-300" },
  { time: "10:30", label: "Outdoor + Yoga", note: "Movement, fresh air, breathing", color: "bg-emerald-100 text-emerald-800", ring: "border-emerald-300" },
  { time: "11:15", label: "Art & Music", note: "Colours, songs, tabla claps", color: "bg-fuchsia-100 text-fuchsia-800", ring: "border-fuchsia-300" },
  { time: "12:00", label: "Reflection & Goodbye", note: "Gratitude, hugs, home time", color: "bg-yellow-100 text-yellow-800", ring: "border-yellow-300" }
];

export const LearningDomainsInfographic = () => (
  <section className="py-16 bg-white" data-testid="learning-domains-infographic">
    <div className="container mx-auto px-4">
      <div className="max-w-3xl mx-auto text-center mb-12">
        <span className="inline-block text-[10px] font-bold text-orange-700 tracking-widest uppercase mb-2">How We Nurture</span>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-800 mb-3">Six Petals of a Whole Child</h2>
        <p className="text-stone-600 text-sm md:text-base">
          At Aadi Gayanam, we don't just teach ABCs. We grow the whole child — head, heart, hands and habits — across six vital petals.
        </p>
      </div>

      <div className="relative max-w-5xl mx-auto">
        {/* central bloom */}
        <div className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-40 h-40 rounded-full bg-gradient-to-br from-orange-500 to-red-600 items-center justify-center shadow-2xl">
          <div className="text-center px-3">
            <p className="font-devanagari text-3xl text-white leading-none mb-1">बालक</p>
            <p className="text-xs text-orange-100 mt-1 tracking-widest font-semibold">THE CHILD</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {domains.map((d, i) => {
            const Icon = d.icon;
            return (
              <div
                key={d.name}
                className={`p-5 rounded-2xl bg-white border-2 border-orange-50 shadow-md hover:shadow-2xl transition-all hover:-translate-y-1 ring-4 ${d.ring} ${
                  i === 1 || i === 4 ? "lg:mt-16" : ""
                }`}
                data-testid={`domain-${d.name.toLowerCase()}`}
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${d.color} flex items-center justify-center shadow-md mb-3`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display text-lg font-bold text-stone-800 mb-1">{d.name}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{d.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

export const DailyScheduleInfographic = () => (
  <section className="py-16 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 relative overflow-hidden" data-testid="daily-schedule-infographic">
    <div className="absolute inset-0 opacity-10 pointer-events-none">
      <div className="absolute top-20 left-10 w-40 h-40 border-2 border-orange-400 rounded-full" />
      <div className="absolute bottom-10 right-20 w-56 h-56 border-2 border-amber-400 rounded-full" />
    </div>
    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-3xl mx-auto text-center mb-12">
        <span className="inline-block text-[10px] font-bold text-orange-700 tracking-widest uppercase mb-2">A Day at Aadi Gayanam</span>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-800 mb-3">A Joyful Day, Beautifully Balanced</h2>
        <p className="text-stone-600 text-sm md:text-base">
          Every moment of the day is designed to nourish the head, heart, hands and habits of your child.
        </p>
      </div>

      <div className="relative max-w-4xl mx-auto">
        {/* connecting line */}
        <div className="absolute left-6 md:left-1/2 top-4 bottom-4 w-0.5 bg-gradient-to-b from-orange-300 via-amber-400 to-yellow-300 md:-translate-x-1/2" />

        <div className="space-y-4">
          {schedule.map((s, i) => (
            <div key={s.time} className={`relative flex ${i % 2 === 0 ? "md:justify-start" : "md:justify-end"}`} data-testid={`schedule-${i}`}>
              {/* dot */}
              <div className={`absolute left-6 md:left-1/2 top-4 w-4 h-4 rounded-full bg-white border-2 ${s.ring} md:-translate-x-1/2 z-10`} />
              {/* card */}
              <div className={`ml-14 md:ml-0 md:w-[46%] p-4 rounded-2xl bg-white shadow-md border ${s.ring} hover:shadow-xl transition-all hover:-translate-y-0.5`}>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center justify-center w-14 h-9 rounded-full text-xs font-bold ${s.color} shadow-inner`}>
                    {s.time}
                  </span>
                  <div>
                    <p className="font-display font-bold text-stone-800 text-base leading-tight">{s.label}</p>
                    <p className="text-xs text-stone-600">{s.note}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const ProgramsInfographics = () => (
  <>
    <LearningDomainsInfographic />
    <DailyScheduleInfographic />
  </>
);

export default ProgramsInfographics;
